import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ListTransactionsInput,
  ListTransactionsResponse,
  SyncTransactionsInput,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export function useTransactionsList(filters: ListTransactionsInput = {}) {
  const query = useQuery(
    orpc.finance.transactions.list.queryOptions({
      input: filters,
    }),
  );

  const transactions = query.data?.transactions ?? [];
  const total = query.data?.total ?? 0;
  const lastSyncTime = query.data?.lastSyncTime ?? null;

  return {
    ...query,
    transactions,
    total,
    lastSyncTime,
  } satisfies typeof query & {
    data?: ListTransactionsResponse;
    transactions: ListTransactionsResponse['transactions'];
    total: number;
    lastSyncTime: string | null;
  };
}

export function useTransactionsSync() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<{ synced: number }, Error, SyncTransactionsInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.transactions.sync(input);
    },
    onSuccess: (data) => {
      showToast({
        title: 'Success',
        message: `Synced ${data.synced} transactions`,
        color: 'success',
        icon: 'check',
      });

      // Invalidate transactions list to refresh the data
      void queryClient.invalidateQueries({
        queryKey: orpc.finance.transactions.list.queryKey({
          input: {},
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to sync transactions',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
